import {
    ColorRedDark2 as primaryMusclesColor,
    ColorYellowDark1 as secondaryMusclesColor,
    ColorLightuiAccent1 as untargetedMusclesColor
} from 'ui-connect/styles/default-theme';

import { hexToRgba } from 'utils/ColorsUtil';
import { removeDuplicates } from 'utils/ArrayUtils';

import Types from 'pages/workouts/util/Types';
import { getExerciseCategoryAndType } from 'utils/activity/ExerciseSetsUtil';

const LottieMuscles = {
    BackNeck: 'BackNeck',
    BackTriceps: 'BackTriceps',
    BackForearms: 'BackForearms',
    BackShoulders: 'BackShoulders',
    BackTraps: 'BackTraps',
    BackLats: 'BackLats',
    BackLowerBack: 'BackLowerBack',
    BackAbductors: 'BackAbductors',
    BackAdductors: 'BackAdductors',
    BackGlutes: 'BackGlutes',
    BackHamstrings: 'BackHamstrings',
    BackCalves: 'BackCalves',
    FrontShoulders: 'FrontShoulders',
    FrontChest: 'FrontChest',
    FrontBiceps: 'FrontBiceps',
    FrontForearms: 'FrontForearms',
    FrontObliques: 'FrontObliques',
    FrontAbs: 'FrontAbs',
    FrontHips: 'FrontHips',
    FrontQuads: 'FrontQuads'
};

// keeping an array for each muscle, if we need to map more than one from Lottie
const GCMuscles = {
    ABS: [LottieMuscles.FrontAbs],
    BICEPS: [LottieMuscles.FrontBiceps],
    CALVES: [LottieMuscles.BackCalves],
    CHEST: [LottieMuscles.FrontChest],
    FOREARM: [LottieMuscles.FrontForearms, LottieMuscles.BackForearms],
    GLUTES: [LottieMuscles.BackGlutes],
    HAMSTRINGS: [LottieMuscles.BackHamstrings],
    ABDUCTORS: [LottieMuscles.BackAbductors],
    ADDUCTORS: [LottieMuscles.BackAdductors],
    HIPS: [LottieMuscles.FrontHips],
    LATS: [LottieMuscles.BackLats],
    LOWER_BACK: [LottieMuscles.BackLowerBack],
    OBLIQUES: [LottieMuscles.FrontObliques],
    QUADS: [LottieMuscles.FrontQuads],
    SHOULDERS: [LottieMuscles.FrontShoulders, LottieMuscles.BackShoulders],
    TRAPS: [LottieMuscles.BackTraps],
    TRICEPS: [LottieMuscles.BackTriceps],
    NECK: [LottieMuscles.BackNeck]
};

export const mapGCMuscleGroupsToLottieLayers = (musclesArray) => {
    if (!musclesArray) {
        return [];
    }

    return musclesArray.map((muscle) => GCMuscles[muscle]).flat();
};

export const getPrimaryMusclesColor = () => hexToRgba(primaryMusclesColor);

export const getSecondaryMusclesColor = () => hexToRgba(secondaryMusclesColor);

export const getUntargetedMusclesColor = () => hexToRgba(untargetedMusclesColor);

export const notSupportedStepTypesForMuscles = [
    Types.StepTypes.Warm_Up,
    Types.StepTypes.Cool_Down,
    Types.StepTypes.Recovery
];

export const getMuscleMapsForWorkoutExercises = (workoutExercises, exerciseTypes) => {
    let primaryMuscles = [],
        secondaryMuscles = [];

    const nonNullWorkoutExercises = workoutExercises.filter(
        (ex) => ex !== null && ex !== undefined && (ex.categoryKey || ex.exerciseKey)
    );

    exerciseTypes.forEach((exerciseType) => {
        const shouldAddMuscles = nonNullWorkoutExercises.find(
            (ex) => ex.categoryKey === exerciseType.categoryKey && ex.exerciseKey === exerciseType.exerciseKey
        );

        if (shouldAddMuscles) {
            if (exerciseType.primaryMuscles) {
                primaryMuscles = primaryMuscles.concat(exerciseType.primaryMuscles);
            }
            if (exerciseType.secondaryMuscles) {
                secondaryMuscles = secondaryMuscles.concat(exerciseType.secondaryMuscles);
            }
        }
    });

    primaryMuscles = removeDuplicates(primaryMuscles);
    secondaryMuscles = removeDuplicates(secondaryMuscles);

    return {
        primaryMuscles,
        secondaryMuscles
    };
};

export const getExerciseDetailsFromActivitySets = (activityReps, activityWorkouts) => {
    const exercisesDetails = [];

    const activeReps =
        activityReps && activityReps.length > 0
            ? activityReps.filter(
                  (rep) => rep.setType !== 'REST' && rep.repetitionCount !== null && rep.repetitionCount >= 0
              )
            : [];

    activeReps.forEach((rep) => {
        const { wktStepIndex, exercises } = rep;
        let { categoryKey, exerciseKey } = getExerciseCategoryAndType(exercises);
        let stepType = 'ACTIVE';

        // if there is no wktStepIndex, it's an exercise added by the user => display the muscles
        if (wktStepIndex === undefined || wktStepIndex === null) {
            stepType = 'ACTIVE';
        }
        // if there is a wktStepIndex, use the details from there to determine the exercise or category key and the step type
        else if (activityWorkouts && activityWorkouts.length > 0) {
            activityWorkouts.forEach((activityWorkout) => {
                // activities created on older devices may have a workout ID (training file message) but no workout or workout step messages
                if (activityWorkout.steps && activityWorkout.steps[wktStepIndex]) {
                    const stepDetails = activityWorkout.steps[wktStepIndex];

                    // if there are several workouts retrieved, choose the one that matches the exercise name and category
                    if (stepDetails.exerciseKey === exerciseKey && stepDetails.categoryKey === categoryKey) {
                        stepType = stepDetails.intensity;

                        exerciseKey = stepDetails.exerciseKey;
                        categoryKey = stepDetails.categoryKey;
                    }
                } else {
                    // if no workout info is available, consider the exercise as being added by the user
                    stepType = 'ACTIVE';
                }
            });
        }

        exercisesDetails.push({ exerciseKey, categoryKey, stepType });
    });

    return exercisesDetails;
};

export const getMuscleMapsFromActivitySets = (activityReps, activityWorkouts, exerciseTypes) => {
    const exercises = getExerciseDetailsFromActivitySets(activityReps, activityWorkouts);
    const supportedExercises = exercises.filter(
        (ex) => !notSupportedStepTypesForMuscles.includes(ex.stepType.toLowerCase())
    );

    return getMuscleMapsForWorkoutExercises(supportedExercises, exerciseTypes);
};

export const getAllMuscleLayers = () => {
    return Object.values(LottieMuscles);
};

/**
 * @param {string[]} primaryMuscles
 * @param {boolean} showNumberOfMuscleGroups
 * @param {Function} t
 * @returns {string}
 */
export const getMuscleMapsString = (primaryMuscles, showNumberOfMuscleGroups, t) => {
    if (!primaryMuscles?.length || !t) {
        return '';
    }

    if (showNumberOfMuscleGroups && primaryMuscles.length > 3) {
        return t('workout:number_of_muscle_groups', { 0: primaryMuscles.length });
    }

    return primaryMuscles.map((muscle) => t(`exercise_types:muscle_type_${muscle}`)).join(', ');
};
